import React from 'react'
import clsx from 'clsx'
import { withStyles, WithStyles, createStyles } from '@mui/styles'
import { Dialog, Divider, IconButton, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { Theme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import { Colors, fontWeights } from '../../theme'
import { GreenButton, GreenButtonProps } from '../Buttons/GreenButton'
import { OutlinedButton } from '../Buttons/OutlinedButton'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      zIndex: 100,
      margin: 0,
      padding: theme.spacing(3),
      paddingTop: theme.spacing(6),
      marginBottom: theme.spacing(3),
    },
    dialogTitle: {
      lineHeight: 0,
      fontSize: 32,
      color: '#FFFFFF',
      fontWeight: fontWeights['400'],
      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
      letterSpacing: ' -0.01562em',
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: '#afb0b2',
    },
    dialog: (props: IStyles) => ({
      background: Colors.darkGrey,
      maxWidth: '100%',
      width: props.width || 500,
      borderRadius: 10,
    }),
    dialogWidthAuto: {
      width: 'auto',
    },
    divider: {
      background: theme.palette.primary.light,
      width: '100%',
      marginBottom: theme.spacing(3),
    },
    wrapBtn: {
      width: '100%',
      textAlign: 'right',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  })

interface IStyles {
  width?: number
  isWidthAuto?: boolean
}

interface IModal extends WithStyles<typeof styles> {
  isOpen: boolean
  isWidthAuto?: boolean
  width?: number
  modalClose: () => void
  // eslint-disable-next-line
  onSubmit?: (e?: any) => void
  onCancel?: () => void
  title: string
  submitTitle?: string
  cancelTitle?: string
  children: React.ReactNode
  actions?: React.ReactNode
  isSubmit?: boolean
  isSubmitDisabled?: boolean
  submitButtonProps?: GreenButtonProps & { visible?: boolean }
}

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string
  children: React.ReactNode
  onClose: () => void
}

const MuiDialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props
  return (
    <DialogTitle className={classes.root} {...other}>
      <span className={classes.dialogTitle}>{children}</span>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  )
})

const MuiDialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
}))(DialogContent)

const MuiDialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: 0,
    paddingBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
}))(DialogActions)

export const Modal = withStyles(styles)((props: IModal) => {
  const {
    classes,
    modalClose,
    onSubmit,
    onCancel,
    isOpen,
    title,
    submitTitle,
    cancelTitle,
    children,
    actions,
    isWidthAuto,
    isSubmitDisabled,
    submitButtonProps,
  } = props

  const { visible = true } = submitButtonProps || {}

  return (
    <Dialog
      classes={{ paper: clsx(classes.dialog, isWidthAuto ? classes.dialogWidthAuto : undefined) }}
      onClose={modalClose}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <MuiDialogTitle id="customized-dialog-title" onClose={modalClose}>
        {title}
      </MuiDialogTitle>

      <MuiDialogContent>{children}</MuiDialogContent>

      <MuiDialogActions>
        {!actions && (
          <>
            <Divider className={classes.divider} />
            <div className={classes.wrapBtn}>
              <OutlinedButton
                width={120}
                text={cancelTitle || 'Cancel'}
                type="button"
                onClick={onCancel || modalClose}
                mr={15}
                tabIndex={1}
              />
              {visible && (
                <GreenButton
                  disabled={isSubmitDisabled}
                  width={120}
                  text={submitTitle || 'Submit'}
                  onClick={onSubmit}
                  tabIndex={2}
                  {...submitButtonProps}
                />
              )}
            </div>
          </>
        )}
      </MuiDialogActions>
    </Dialog>
  )
})
